import {
    pickAppCopy,
    type SupportedLocale,
    getTitleSelectOptions,
    formatTitle,
    type TitleSelectOption,
} from "@patient-watch/i18n";
import { useMemo } from "react";
import { useAppLocale } from "./LocaleContext";
import { inviteCopyEn } from "@patient-watch/i18n/webapp/en-GB/inviteCopy";
import { inviteCopyDe } from "@patient-watch/i18n/webapp/de-DE/inviteCopy";
import { inviteCopyDa } from "@patient-watch/i18n/webapp/da-DK/inviteCopy";
import { inviteCopyEnUs } from "@patient-watch/i18n/webapp/en-US/inviteCopy";
import { inviteCopyFr } from "@patient-watch/i18n/webapp/fr-FR/inviteCopy";
import { inviteCopyIt } from "@patient-watch/i18n/webapp/it-IT/inviteCopy";
import { inviteCopyEs } from "@patient-watch/i18n/webapp/es-ES/inviteCopy";

export interface InviteCopy {
    reviewSetup: string;
    organisationAndContact: string;
    addPatient: string;
    inviteByEmail: string;
    shareQrCode: string;
    inviteSent: string;
    inviteSentTo: (email: string) => string;
    loading: string;
    justAMoment: string;
    patientEmailWillReceiveInstructions: (email: string) => string;
    patientPhoneWillReceiveInstructions: (phone: string) => string;
    patientWillReceiveInstructions: string;
    emailAndMobileHint: string;
    emailOrPhoneRequired: string;
    mobileOrEmailRequired: string;
    diariesAddedInLast7Days: (count: number) => string;
    goToDiary: string;
    inviteAnotherPatient: string;
    whatWasInjected: string;
    clinicians: string;
    referringClinician: string;
    injectingClinician: string;
    operatingSurgeon: string;
    therapist: string;
    injectionMethod: string;
    questionnaires: string;
    clinicContact: string;
    surgeryDetails: string;
    injectionDetails: string;
    treatmentDetails: string;
    consultationDetails: string;
    consultationName: string;
    description: string;
    clinician: string;
    consultingClinician: string;
    consultingClinicianDescription: string;
    productDetails: string;
    providingClinician: string;
    providingClinicianDescription: string;
    usageInstructions: string;
    editInjectionDetails: string;
    editInjectionClinicians: string;
    editSurgeryDetails: string;
    editSurgeryClinicians: string;
    editShockwaveDetails: string;
    editShockwaveClinicians: string;
    editConsultationDetails: string;
    editConsultingClinician: string;
    editProductDetails: string;
    editProvidingClinician: string;
    editClinicContact: string;
    notSet: string;
    selected: string;
    unknownForm: string;
    unknownSchedule: string;
    noSide: string;
    // Add patient step
    email: string;
    enterEmail: string;
    titleOptional: string;
    firstNameOptional: string;
    lastNameOptional: string;
    mobileNumber: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    selectPlaceholder: string;
    sendInvite: string;
    sendingInvite: string;
    inviteSentToast: string;
    existingPatient: string;
    existingPatientAddDiary: string;
    existingUser: string;
    existingUserAddToOrganisation: string;
    existingSecretary: string;
    existingSecretaryAddAsYours: string;
    diaryQuotaBlockedTitle: string;
    diaryQuotaUsage: (used: number, limit: number) => string;
    diaryQuotaBlockedDescription: string;
    diaryQuotaUpgradeCta: string;
    diaryQuotaOwnerUnresolved: string;
    noTemplateSelected: string;
    createTemplate: string;
    createTemplateFirst: string;
    organisation: string;
    hospitalNumberOptional: string;
    enterNhsHospitalNumber: string;
    hospitalNumberDescription: string;
    /** The invite's choice of which date follow-ups count from, pre-set from the template. */
    referenceDateLabel: string;
    referenceDateStartToday: string;
    referenceDateAskPatient: string;
    referenceDateStartTodayHint: string;
    referenceDateAskPatientHint: string;
    selectOrganisationToContinue: string;
    selectContactToContinue: string;
    doctorEmailSharedWithPatient: string;
    enterPhoneNumber: string;
    backToSetup: string;
    patientInvite: string;
    diaryQrCode: string;
    diaryQrDescription: string;
    generatingInviteLink: string;
    failedToCreateInviteLink: string;
    hospitalOnTheInvite: string;
    changingOrganisationUpdatesLink: string;
    shownOnPatientSignupLink: string;
    patientWillChooseOrganisation: string;
    hospitalNumber: string;
    enterHospitalNumber: string;
    requiredForNhsOrganisations: string;
    copyLink: string;
    copyQrCode: string;
    copyInviteLink: string;
    invitePageTitle: string;
    roleAndOrganisation: string;
    navInviteDescription: string;
    tabEmail: string;
    tabQrCode: string;
    optionalSuffix: string;
    titleOptions: TitleSelectOption[];
    formatTitle: (title: string | null | undefined) => string;
}

type InviteCopyBase = Omit<InviteCopy, "titleOptions" | "formatTitle">;

const inviteCopy: Record<SupportedLocale, InviteCopyBase> = {
    "en-GB": inviteCopyEn,
    "en-US": inviteCopyEnUs,
    "de-DE": inviteCopyDe,
    "da-DK": inviteCopyDa,
    "fr-FR": inviteCopyFr,
    "it-IT": inviteCopyIt,
    "es-ES": inviteCopyEs,
};

export function useInviteCopy(): InviteCopy {
    const { locale } = useAppLocale();
    return useMemo(
        () => ({
            ...pickAppCopy(inviteCopy, locale),
            titleOptions: getTitleSelectOptions(locale),
            formatTitle: (title: string | null | undefined) => formatTitle(title, locale),
        }),
        [locale]
    );
}

export function getInviteCopy(locale: SupportedLocale): InviteCopy {
    return {
        ...pickAppCopy(inviteCopy, locale),
        titleOptions: getTitleSelectOptions(locale),
        formatTitle: (title: string | null | undefined) => formatTitle(title, locale),
    };
}
